import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useApptekster } from '../../contexts/ApptekstContext';
import {
    AktivSakStatus,
    DineSkiltSakStatus,
    SakStatus,
    sendFjernKnytning,
} from '../../utils/api';
import { useApi } from '../../hooks/useApi';
import { HotjarMaskert } from '../../components/HotjarMaskert';
import { Paragraph } from '../../components/Paragraph';
import { useKnytningState } from '../knytning/KnyttSkilt';
import { LastNedFil } from '../knytning/LastNedFil';
import './KnyttSkiltSteg.scss';
import { GodtaVilkarSkjema } from '../../components/GodtaVilkarSkjema';
import { PakrevdFelt } from '../../components/PakrevdFelt';
import { SVVButton, SVVButtonRow } from '@komponentkassen/svv-button';

type KnyttSkiltStegProps = {
    sakStatus: SakStatus;
    tegnkombinasjon: string;
    rettighetId: number;
    kjennemerke?: string;
};

export const KnyttSkiltSteg: React.FC<KnyttSkiltStegProps> = ({
    sakStatus,
    tegnkombinasjon,
    rettighetId,
    kjennemerke,
}) => {
    const t = useApptekster();
    const navigate = useNavigate();
    const [fjernKnytning, setFjernKnytning] = useState(false);
    const setRettighetId = useKnytningState((state) => state.setRettighetId);

    const { hent, data, laster } = useApi(sendFjernKnytning, {
        id: 'fjernKnytning',
    });

    const visLaster = laster || data?.ok;

    const bekreftelsestekst = `${t(
        'personlig-kjennemerke.status.knytt.fjern-knytning.vilkar.del1',
    )} ${tegnkombinasjon} ${t(
        'personlig-kjennemerke.status.knytt.fjern-knytning.vilkar.del2',
    )} ${kjennemerke} ${t(
        'personlig-kjennemerke.status.knytt.fjern-knytning.vilkar.del3',
    )}`;

    useEffect(() => {
        if (data?.ok) {
            window.location.reload();
        }
    }, [data]);

    switch (sakStatus) {
        case AktivSakStatus.MOTTATT:
        case AktivSakStatus.INNVILGET:
        case AktivSakStatus.BETALT:
        case AktivSakStatus.SKILT_MOTTATT:
        case AktivSakStatus.SKILT_BESTILT:
        case AktivSakStatus.SKILT_BESTILT_BRUKER:
            return (
                <li className="statusliste__steg">
                    <h3 className="svv-h5">
                        {t('personlig-kjennemerke.status.knytt.overskrift')}
                    </h3>
                </li>
            );
        case DineSkiltSakStatus.SKILT_UTLEVERT:
            return (
                <li className="statusliste__steg aktiv">
                    <h3 className="svv-h5 svv-h5--spacing">
                        {' '}
                        {t('personlig-kjennemerke.status.knytt.knytt-skilt')}
                    </h3>
                    <Paragraph>
                        {t(
                            'personlig-kjennemerke.status.knytt.knytt-skilt.hva-skjer-videre',
                        )}
                    </Paragraph>
                    <SVVButton
                        onClick={() => {
                            setRettighetId(`${rettighetId}`);
                            navigate(`/status/knytt/${tegnkombinasjon}`);
                        }}
                    >
                        {t(
                            'personlig-kjennemerke.status.knytt.knytt-til-kjoretoy',
                        )}
                    </SVVButton>
                </li>
            );
        case DineSkiltSakStatus.KNYTTET:
            return (
                <li className="statusliste__steg fullfort">
                    <h3 className="svv-h5 svv-h5--spacing">
                        {t(
                            'personlig-kjennemerke.status.knytt.knyttet-skilt.del1',
                        )}{' '}
                        <HotjarMaskert>{tegnkombinasjon}</HotjarMaskert>{' '}
                        {t(
                            'personlig-kjennemerke.status.knytt.knyttet-skilt.del2',
                        )}{' '}
                        <HotjarMaskert>{kjennemerke}</HotjarMaskert>
                    </h3>
                    {!fjernKnytning && (
                        <>
                            <Paragraph>
                                {t(
                                    'personlig-kjennemerke.status.knytt.knyttet.hva-skjer-videre',
                                )}
                            </Paragraph>
                            <Paragraph>
                                {t(
                                    'personlig-kjennemerke.status.knytt.knyttet.hva-skjer-videre.info',
                                )}
                            </Paragraph>
                            <SVVButtonRow>
                                <LastNedFil rettighetId={rettighetId} />
                                <SVVButton
                                    onClick={() => {
                                        setFjernKnytning(true);
                                    }}
                                >
                                    {t(
                                        'personlig-kjennemerke.status.knytt.fjern-knytning',
                                    )}
                                </SVVButton>
                            </SVVButtonRow>
                        </>
                    )}
                    {fjernKnytning && (
                        <div className="fjern-knytning">
                            <PakrevdFelt className="spacing-bottom--xxsmall" />
                            <GodtaVilkarSkjema
                                handlingsknapp={t(
                                    'personlig-kjennemerke.status.knytt.fjern-knytning.vilkar.fjern-knytning',
                                )}
                                bekreftelseslabel={t(
                                    'personlig-kjennemerke.fjern-knytning.sjekkboks.label',
                                )}
                                bekreftelsestekst={bekreftelsestekst}
                                laster={!!visLaster}
                                onSubmit={() =>
                                    hent({
                                        rettighetId,
                                        kjennemerke,
                                    })
                                }
                                avbrytKnapp={() => {
                                    setFjernKnytning(false);
                                }}
                            />
                        </div>
                    )}
                </li>
            );
        default:
            return <></>;
    }
};
